import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import NavBar from './components/NavBar';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import Details from './pages/Details';
import Settings from './pages/Settings';
import TrainingDataPage from './pages/TrainingDataPage';
import AnonymousChatPage from './pages/AnonymousChatPage';
import { useDispatch } from 'react-redux';
import { AppDispatch } from './store/store';
import { fetchProjects } from './store/projectSlice';
import { syncUser, clearUser } from './store/userSlice';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth } from './hooks/useAuth';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minWidth: '320px',
        },
      },
    },
  },
});

function App() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProjects());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(syncUser(user));
    } else {
      dispatch(clearUser());
    }
  }, [dispatch, isAuthenticated, user]);


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Anonymous chat routes - no layout */}
        <Route path="/anon-chat" element={<AnonymousChatPage />} />
        <Route path="/:orgToken/anon-chat" element={<AnonymousChatPage />} />
        
        {/* Main application routes with layout */}
        <Route path="/*" element={
          <Box sx={{
            display: 'flex',
            minWidth: '320px',
            overflow: 'auto'
          }}>
            <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: 'calc(100vh - 72px)',
              ml: { xs: 0, md: '260px' },
              mt: '72px',
              bgcolor: 'background.default',
              display: 'flex',
              transition: 'margin 0.3s ease-in-out',
              minWidth: 0, // Allow content to shrink below parent width
            }}
          >
            <NavBar onSettingsClick={() => setSettingsOpen(true)} />
            <Box sx={{ 
              p: { xs: 1, sm: 2, md: 3 },  
              width: '100%', 
              minWidth: 0 
            }}>
              <Routes>
                <Route path="/" element={<ProtectedRoute component={Home} />} />
                <Route path="/:orgToken" element={<ProtectedRoute component={Home} />} />
                <Route path="/details" element={<ProtectedRoute component={Details} />} />
                <Route path="/:orgToken/details" element={<ProtectedRoute component={Details} />} />
                <Route path="/training-data" element={<ProtectedRoute component={TrainingDataPage} />} />
                <Route path="/:orgToken/training-data" element={<ProtectedRoute component={TrainingDataPage} />} />
                <Route path="/settings" element={<ProtectedRoute component={Settings} />} />
                <Route path="/:orgToken/settings" element={<ProtectedRoute component={Settings} />} />
              </Routes>
            </Box>
          </Box>
          <Settings
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
        </Box>
        } />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
